/**
 * Patient Onboarding (Patient Master) API service
 *
 * Wire-level response types come from `src/shared/api/swagger.d.ts`
 * (auto-generated via `npm run gen:types`, default source
 * `https://preprod-portalservice.hellohealth.com/v3/api-docs`, overridable
 * via `SWAGGER_URL`).
 *
 * Endpoint -> swagger operation -> wire response schema
 *   POST /patient/invite         invitePatient        APIResponsePortalPatientInvitationLogDTO  (req: PortalPatientInvitationLogDTO)
 *   GET  /patient/inviteList     patientInviteList    APIResponsePortalPatientInvitationLogDTO
 *   POST /practice/linkPatient   linkPatient          APIResponseString                         (req: ReqUserPatientMap)
 *   GET  /patient/invite/decline updateInviteStatus   APIResponseString
 *
 * Discrepancies vs. local types:
 * - `linkPatient` and `updateInviteStatus` are typed at the wire as
 *   `APIResponseString`; the FE consumes a structured envelope
 *   (`success`/`responseMsg`/`responseCode`) — kept as local types and cast
 *   at the boundary.
 */
import axios from '../../../app/axios';
import type { components } from '@/shared/api/swagger';
import {
  PatientInviteRequest,
  PatientInviteResponse,
  InviteListRequest,
  InviteListResponse,
  InviteDisplayData,
  InviteListItem,
  PaginationParams,
  LinkPatientRequest,
  LinkPatientResponse,
  DeclinePatientRequest,
  DeclinePatientResponse
} from '../types';
import { toTitleCase } from '@/shared/utils/stringUtils';

type Schemas = components['schemas'];

// Patient Invite API Functions
export const sendPatientInviteApi = async (payload: PatientInviteRequest): Promise<PatientInviteResponse> => {
  const response = await axios.post<Schemas['APIResponsePortalPatientInvitationLogDTO']>("/patient/invite", payload);
  return response.data as unknown as PatientInviteResponse;
};

// Helper function to format date to YYYY-MM-DD format
const formatDateForAPI = (dateInput: string): string => {
  try {
    // Handle both ISO datetime strings and date-only strings
    const date = new Date(dateInput);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
    
    // Format to YYYY-MM-DD (LocalDate format expected by backend)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error, 'Input:', dateInput);
    throw new Error(`Invalid date format: ${dateInput}`);
  }
};

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const parseYmd = (value: string): { year: string; month: number; day: number } | null => {
  const match = value.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [, year, month, day] = match;
  const monthNum = Number(month);
  const dayNum = Number(day);
  if (monthNum < 1 || monthNum > 12 || Number.isNaN(dayNum)) return null;

  return { year, month: monthNum, day: dayNum };
};

const formatLongDateNoTimezone = (value: string): string => {
  const parsed = parseYmd(value);
  if (!parsed) return value;
  return `${MONTHS_LONG[parsed.month - 1]} ${String(parsed.day).padStart(2, '0')}, ${parsed.year}`;
};

const formatShortDateNoTimezone = (value: string): string => {
  const parsed = parseYmd(value);
  if (!parsed) return value;
  return `${MONTHS_SHORT[parsed.month - 1]} ${String(parsed.day).padStart(2, '0')}, ${parsed.year}`;
};

// Build request payload for patient invite
export const buildPatientInviteRequest = (
  firstName: string,
  lastName: string,
  dateOfBirth: string,
  email: string,
  mobileNumber: string,
  userContext: {
    portalAccountId: number;
    userId: number;
  }
): PatientInviteRequest => {
  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    dateOfBirth: formatDateForAPI(dateOfBirth), // Convert to YYYY-MM-DD format
    email: email.trim(),
    mobileNumber: mobileNumber.trim(),
    portalAccountId: userContext.portalAccountId, // Dynamic portal account ID from user context
    userId: userContext.userId // Dynamic user ID from user context
  };
};

// Main service function to send patient invite
export const sendPatientInvite = async (
  firstName: string,
  lastName: string,
  dateOfBirth: string,
  email: string,
  mobileNumber: string,
  userContext: {
    portalAccountId: number;
    userId: number;
  }
): Promise<{
  success: boolean;
  message: string;
  responseCode?: string | null;
  existingPatient: PatientInviteResponse['data'];
}> => {
  try {
    const request = buildPatientInviteRequest(firstName, lastName, dateOfBirth, email, mobileNumber, userContext);
    const response = await sendPatientInviteApi(request);
    
    if (!response.success) {
      throw new Error(response.responseMsg || 'Failed to send invitation');
    }
    
    return {
      success: true,
      message: response.responseMsg,
      responseCode: response.responseCode,
      existingPatient: response.totalCount > 0 ? response.data : null
    };
  } catch (error) {
    console.error('Error sending patient invite:', error);
    throw error;
  }
};

// Build request payload for resend patient invite
export const buildResendPatientInviteRequest = (
  firstName: string,
  lastName: string,
  dateOfBirth: string,
  email: string,
  mobileNumber: string,
  partnerPatientId: string,
  userContext: {
    portalAccountId: number;
    userId: number;
  }
): PatientInviteRequest & { mobileNumber: string; partnerPatientId: string } => {
  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    dateOfBirth: formatDateForAPI(dateOfBirth),
    email: email.trim(),
    mobileNumber: mobileNumber.trim(),
    partnerPatientId: partnerPatientId.trim(),
    portalAccountId: userContext.portalAccountId,
    userId: userContext.userId
  };
};

// Main service function to resend patient invite
export const resendPatientInvite = async (
  firstName: string,
  lastName: string,
  dateOfBirth: string,
  email: string,
  mobileNumber: string,
  partnerPatientId: string,
  userContext: {
    portalAccountId: number;
    userId: number;
  }
): Promise<{
  success: boolean;
  message: string;
  responseCode?: string | null;
}> => {
  try {
    const request = buildResendPatientInviteRequest(
      firstName, 
      lastName, 
      dateOfBirth, 
      email, 
      mobileNumber, 
      partnerPatientId, 
      userContext
    );
    const response = await sendPatientInviteApi(request);
    
    if (!response.success) {
      throw new Error(response.responseMsg || 'Failed to resend invitation');
    }
    
    return {
      success: true,
      message: response.responseMsg,
      responseCode: response.responseCode
    };
  } catch (error) {
    console.error('Error resending patient invite:', error);
    throw error;
  }
};

// Invite List API Functions
export const getInviteListApi = async (params: InviteListRequest): Promise<InviteListResponse> => {
  const response = await axios.get<Schemas['APIResponsePortalPatientInvitationLogDTO']>("/patient/inviteList", { params });
  return response.data as unknown as InviteListResponse;
};

// Transform invite API response to UI display format
export const transformInviteData = (inviteItems: InviteListItem[]): InviteDisplayData[] => {
  return inviteItems.map((item) => {
    // Format full name
    const fullName = `${item.firstName} ${item.lastName}`.trim();
    
    // Keep existing UI format while avoiding timezone conversion
    const formattedDOB = item.dateOfBirth ? formatLongDateNoTimezone(item.dateOfBirth) : '';
    const formattedSentDate = item.creationDate ? formatShortDateNoTimezone(item.creationDate) : '';
    const formattedReturnedDate = item.modificationDate && item.modificationDate !== item.creationDate
      ? formatShortDateNoTimezone(item.modificationDate)
      : '';
    
    // Format "sent by" from author info
    const sentBy = `${item.authorFirstName} ${item.authorLastName}`.trim();
    
    return {
      id: item.portalPatientInvitationLogId,
      name: fullName,
      dob: formattedDOB,
      email: item.email || '',
      phone: item.mobileNumber || '',
      dateSent: formattedSentDate,
      dateReturned: formattedReturnedDate,
      sentBy: sentBy,
      status: toTitleCase(item.status),
      avatar: '/images/img_avatar_placeholder.png', // Default avatar
      partnerPatientId: item.partnerPatientId || '',
      portalPatientInvitationLogId: item.portalPatientInvitationLogId,
      userId: item.userId,
      isLinkedPatient: item.isLinkedPatient,
      modifiedBy: item.modifiedBy,
      active: item.active
    };
  });
};

// Build request parameters for invite list
export const buildInviteListRequest = (
  pagination: PaginationParams,
  inviteType: 'RECEIVED' | 'SENT',
  userContext: {
    portalAccountId: number;
    userId: number;
  }
): InviteListRequest => {
  return {
    pageNo: pagination.page,
    pageSize: pagination.pageSize,
    portalAccountId: userContext.portalAccountId, // Dynamic portal account ID from user context
    userId: userContext.userId, // Dynamic user ID from user context
    inviteType: inviteType // Dynamic invite type (RECEIVED or SENT)
  };
};

// Main service function to get invite list with pagination
export const getInviteList = async (
  pagination: PaginationParams = { page: 0, pageSize: 10 },
  inviteType: 'RECEIVED' | 'SENT',
  userContext: {
    portalAccountId: number;
    userId: number;
  }
): Promise<{
  data: InviteDisplayData[];
  total: number;
  success: boolean;
}> => {
  try {
    const request = buildInviteListRequest(pagination, inviteType, userContext);
    const response = await getInviteListApi(request);
    
    if (!response.success) {
      throw new Error('Invite list API request failed');
    }
    
    // Transform data
    const transformedData = transformInviteData(response.list);
    
    return {
      data: transformedData,
      total: response.totalCount,
      success: true
    };
  } catch (error) {
    console.error('Error fetching invite list:', error);
    throw error;
  }
};

// Link Patient API Functions
export const linkPatientApi = async (payload: LinkPatientRequest): Promise<LinkPatientResponse> => {
  const response = await axios.post<Schemas['APIResponseString']>("/practice/linkPatient", payload);
  return response.data as unknown as LinkPatientResponse;
};

// Main service function to link patient
export const linkPatient = async (
  userId: number,
  portalAccountsId: number
): Promise<{
  success: boolean;
  message: string | null;
  responseCode?: string | null;
}> => {
  try {
    const request: LinkPatientRequest = {
      userId,
      partnerPatientId: "",
      partnerMrn: "",
      partnerPcref: "",
      portalAccountsId
    };
    
    console.log('Linking patient:', request);
    const response = await linkPatientApi(request);
    
    console.log('Link patient result:', response);
    
    if (!response.success) {
      throw new Error(response.responseMsg || 'Failed to link patient');
    }
    
    return {
      success: true,
      message: response.responseMsg || 'Patient linked successfully',
      responseCode: response.responseCode
    };
  } catch (error) {
    console.error('Error linking patient:', error);
    throw error;
  }
};

// Decline Patient API Functions
export const declinePatientApi = async (payload: DeclinePatientRequest): Promise<DeclinePatientResponse> => {
  const response = await axios.get<Schemas['APIResponseString']>("/patient/invite/decline", { params: payload });
  return response.data as unknown as DeclinePatientResponse;
};

// Main service function to decline patient
export const declinePatient = async (
  portalPatientInvitationLogId: number,
  modifiedBy: number,
  userId: number,
  active: boolean
): Promise<{
  success: boolean;
  message: string | null;
  responseCode?: string | null;
}> => {
  try {
    const request: DeclinePatientRequest = {
      portalPatientInvitationLogId,
      active,
      modifiedBy,
      userId
    };
    
    console.log('Declining patient:', request);
    const response = await declinePatientApi(request);
    
    console.log('Decline patient result:', response);
    
    if (!response.success) {
      throw new Error(response.responseMsg || 'Failed to decline patient');
    }
    
    return {
      success: true,
      message: response.responseMsg || 'Patient declined successfully',
      responseCode: response.responseCode
    };
  } catch (error) {
    console.error('Error declining patient:', error);
    throw error;
  }
};
